import { useEffect, useState } from "react";
import { postFetcher } from "api/swr";

type Source = { tags: string[]; description: string };
type Display = {
  source: Source;
  tags: Map<string, string>;
  description: string;
  status: string;
  error: string;
};

/** Translate the entire result in one request; English remains the saved source. */
export function useTagResultTranslation(source: Source | null) {
  const [attempt, setAttempt] = useState(0);
  const [display, setDisplay] = useState<Display | null>(null);
  useEffect(() => {
    if (!source) return;
    let cancelled = false;
    const controller = new AbortController();
    const next: Display = {
      source, tags: new Map(), description: "", error: "",
      status: "正在翻译标签…",
    };
    setDisplay(next);
    void (async () => {
      try {
        const result = await postFetcher("/tag-prompt-lab/translate", { arg: source }, { signal: controller.signal });
        if (cancelled) return;
        if (!Array.isArray(result.tags) || result.tags.length !== source.tags.length ||
          result.tags.some((tag: unknown) => typeof tag !== "string" || !tag.trim()) ||
          typeof result.description !== "string") throw new Error("Invalid translation");
        next.tags = new Map(source.tags.map((tag, index) => [tag, result.tags[index]]));
        next.description = result.description;
      } catch {
        next.error = "翻译暂不可用，已保留英文结果，请重试。";
      } finally {
        if (!cancelled) setDisplay({ ...next, status: "" });
      }
    })();
    return () => { cancelled = true; controller.abort(); };
  }, [source, attempt]);
  return {
    retry: () => setAttempt(value => value + 1),
    tags: display?.source === source ? display.tags : new Map<string, string>(),
    description: display?.source === source ? display.description : "",
    status: display?.source === source ? display.status : "",
    error: display?.source === source ? display.error : "",
  };
}
